<template>
  <div class="tenancy-all-projects-wrapper">
    <div class="clearfix tab-header-panel">
      <div class="right-btns">
        <button
          type="button"
          class="btn btn--darkblue"
          role="reset-button"
          @click="resetFilters()"
        >
          {{ $t('action.resetFilter') }}
        </button>
        <button
          id="query-btn"
          type="button"
          class="btn btn--orange"
          role="search-button"
          @click="queryTenancyAllProjects()"
        >
          {{ $t('action.search') }}
        </button>
      </div>
    </div>
    <div>
      <table class="all-projects-table">
        <thead>
          <tr>
            <th
              v-for="header in itemHeaders"
              :key="header.name"
              :class="header.class"
              @click="sortBy(header.attrPath)"
            >
              {{ header.name }} {{ orderbyAttr === header.attrPath ? reverse ? '&#9660;' : '&#9650;' : null }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr id="search-panel">
            <th>
              <div class="date-picker">
                <DatePicker
                  v-model="sortingAttributes.startDay"
                  class="columns date-range-picker"
                  :calendarButton="true"
                  :clearButton="true"
                  calendarButtonIcon="fa fa-calendar"
                  :placeholder="$t('dateFrom')"
                  :disabled="{from: sortingAttributes.endDay}"
                />
                <DatePicker
                  v-model="sortingAttributes.endDay"
                  class="columns date-range-picker"
                  :calendarButton="true"
                  :clearButton="true"
                  calendarButtonIcon="fa fa-calendar"
                  :placeholder="$t('dateTo')"
                  :disabled="{to: sortingAttributes.startDay}"
                />
              </div>
            </th>
            <th class="search-filter-type">
              <HKLSelect
                :value="projectTypeSelectValue"
                :options="typeSelectOptions"
                :showSearch="true"
                placeholder=""
                @change="updateProjectTypeOption"
              />
            </th>
            <th style="max-width: 40px">
              <input
                v-model="sortingAttributes.project.premisesLocation"
                type="text"
              >
            </th>
            <th class="search-filter-type">
              <HKLSelect
                :value="sortingAttributes.project.floor || undefined"
                :options="floorSelectOptions"
                :showSearch="true"
                placeholder=""
                @change="val => { sortingAttributes.project.floor = val || null }"
              />
            </th>
            <th class="search-filter-status">
              <HKLSelect
                :value="sortingAttributes.project.status || undefined"
                :options="statusSelectOptions"
                :showSearch="true"
                placeholder=""
                @change="val => { sortingAttributes.project.status = val || null }"
              />
            </th>
          </tr>
          <tr
            v-for="item in sortedItems"
            :key="item.id"
          >
            <td>{{ item.date || emptyValue }}</td>
            <td>{{ (item.type || emptyValue) | customTProjectTypeLabel }}</td>
            <td>
              <a
                class="link_text"
                @click="navToDetail(item, true)"
              >{{ showPremisesLocation(item) }}</a>
            </td>
            <td>{{ item.floor || emptyValue }}</td>
            <td>{{ (item.status || emptyValue) | customStatusLabel }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import mutationTypes from '@/store/mutation-types'
import EventBus from '@/utils/eventBus'
import dateUtil from '@/utils/dateUtil'
import { hotbTypes, hotbStatus, tenantProjectStatus, tenantProjectTypes } from '@/utils/locale'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import { ALL_VALUE, allTenantProjectsMixin, normalizeSelectFilterValue } from '@/modules/tenantProjects/allTenantProjectsMixin'
import {TENANCY_ALL_PROJECTS_COMPONENT} from '@/utils/consts'
import { getHotbPremises } from '@/modules/hotb/utils'

const sortingAttributes = {
  project: {
    type: null,
    projectType: null,
    premisesLocation: null,
    floor: null,
    status: null,
  },
  startDay: null,
  endDay: null,
}

export default {
  name: TENANCY_ALL_PROJECTS_COMPONENT,
  components: {
    HKLSelect,
  },
  mixins: [allTenantProjectsMixin],
  props: {
    statusList: {
      type: Array,
      default: () => [],
    },
    canAccessProjects: {
      type: Boolean,
      default: false,
    },
    canAccessHotbs: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    const tenantProjectStatusOptions = Object.keys(tenantProjectStatus).map((status) =>
      ({ label: this.$t(status), val: status })).filter(item => item.val !== tenantProjectStatus.CANCELLED)

    return {
      ALL_VALUE,
      projectTypeSelectValue: undefined,
      sortingAttributes: lodash.cloneDeep(sortingAttributes),
      tenantProjectStatusOptions,
      emptyValue: '--',
    }
  },
  computed: {
    ...mapGetters('ccd', ['currentTenancy']),
    ...mapGetters('hotb', ['lastSearchedAttributes', 'allHotbs']),
    ...mapGetters('tenantProject', ['allTenantProjects']),
    ...mapGetters('user', ['isTenant', 'currentUser']),
    itemHeaders () {
      return [{
        attrPath: 'date',
        name: this.$t('tenancyProject.date'),
        class: 'date',
      }, {
        attrPath: 'type',
        name: this.$t('projectType'),
        class: 'project-type',
      }, {
        attrPath: 'premisesLocation',
        name: this.$t('tenancyProject.premises'),
        class: 'premises-location',
      }, {
        attrPath: 'floor',
        name: this.$t('tenancyProject.floor'),
        class: 'floor',
      }, {
        attrPath: 'status',
        name: this.$t('tenancyProject.status'),
        class: 'status',
      }]
    },
    sortedItems () {
      const parsedAllHotbs = this.allHotbs
        .filter((hotb) => !this.statusList.length || this.statusList.includes(hotb.status))
        .map((rec) => {
          return {
            id: rec.id,
            date: rec.scheduledTransactionDate ? dateUtil.formatDate(rec.scheduledTransactionDate, true) : dateUtil.formatDate(rec.transactionDate, false),
            dateForCompare: rec.scheduledTransactionDate ? rec.scheduledTransactionDate : rec.transactionDate,
            type: rec.type,
            premisesLocation: getHotbPremises(rec),
            floor: rec.floor.join(', '),
            status: rec.status,
          }
        })
      const parsedAllTenantProjects = this.allTenantProjects.filter(rec => {
        return (rec.status !== tenantProjectStatus.DRAFT) || (rec.status === tenantProjectStatus.DRAFT && rec.createdBy === this.currentUser.id)
      }).map(rec => {
        return {
          id: rec.id,
          description: rec.description,
          date: rec.statusUpdatedAt ? dateUtil.formatDate(rec.statusUpdatedAt, true) : '',
          dateForCompare: rec.statusUpdatedAt ? rec.statusUpdatedAt : '',
          type: rec.type,
          premisesLocation: rec.projectLocation,
          floor: rec.floors.join(', '),
          status: rec.status,
        }
      })
      const allProjects = parsedAllHotbs.concat(parsedAllTenantProjects)
      const result = lodash.orderBy(allProjects, (obj) => {
        const millis = obj.dateForCompare ? +new Date(obj.dateForCompare) : 0
        return this.orderbyAttr === 'date' ? millis : obj[this.orderbyAttr]
      })
      return this.reverse ? result.reverse() : result
    },
    floorOptions () {
      const floors = deep(this.currentTenancy, 'building.floor') || []
      return floors.map((floor) => ({ label: floor, val: floor }))
    },
    typeSelectOptions () {
      return this.typeOptions.map((opt) => ({
        name: this.$options.filters.customTProjectTypeLabel(opt.label),
        value: opt.val,
      }))
    },
    statusSelectOptions () {
      return this.statusOptions.map((opt) => ({
        name: this.$options.filters.customStatusLabel(opt.label),
        value: opt.val,
      }))
    },
    floorSelectOptions () {
      return (this.floorOptions || []).map((opt) => ({ name: opt.label, value: opt.val }))
    },
  },
  async mounted () {
    $('#search-panel').keydown(function (event) {
      var keypressed = event.keyCode || event.which
      if (keypressed === 13) {
        $('#query-btn').click()
      }
    })
    const lastSearchAttributes = this.lastSearchedAttributes
    if (Object.keys(lastSearchAttributes).length > 0) {
      this.sortingAttributes = lastSearchAttributes
    }
    this.normalizeSelectSortingAttributes()
    EventBus.$on('tenancy.allProjectsTab.query', (query = {}) => {
      this.resetFilters()
      this.sortingAttributes = lodash.merge(this.sortingAttributes, query)
      this.normalizeSelectSortingAttributes()
      this.queryTenancyAllProjects()
    })
  },
  destroyed () {
    EventBus.off('tenancy.allProjectsTab.query')
  },
  methods: {
    ...mapActions('hotb', {
      setHotbLastSearchedAttributes: 'setLastSearchedAttributes',
      loadAllHotbs: 'loadAllHotbs',
    }),
    ...mapMutations('hotb', {
      hotbLoadAll: mutationTypes.HOTB_LOAD_ALL,
    }),
    ...mapActions('tenantProject', {
      setTenantProjectLastSearchedAttributes: 'setLastSearchedAttributes',
      loadAllTenantProjects: 'loadAllTenantProjects',
    }),
    ...mapMutations('tenantProject', {
      tenantProjectLoadAll: mutationTypes.TENANT_PROJECT_LOAD_ALL,
    }),
    showPremisesLocation (item) {
      let locations = item.premisesLocation
      if (!Array.isArray(locations)) {
        locations = [locations].filter(Boolean)
      }
      if (locations.length === 0) return this.emptyValue
      let str = locations.join(',')
      if (item.description) {
        str = [str, item.description].join(' - ')
      }
      return str.trim() ? str : this.emptyValue
    },
    normalizeSelectSortingAttributes () {
      const rawType = this.sortingAttributes.project.type
      this.sortingAttributes.project.type = normalizeSelectFilterValue(rawType) || null
      this.projectTypeSelectValue = rawType === ALL_VALUE ? ALL_VALUE : this.sortingAttributes.project.type || undefined
      this.sortingAttributes.project.floor = normalizeSelectFilterValue(this.sortingAttributes.project.floor) || null
      this.sortingAttributes.project.status = normalizeSelectFilterValue(this.sortingAttributes.project.status) || null
      this.setStatusOptions(this.sortingAttributes.project.type)
    },
    updateProjectTypeOption (typeVal) {
      const val = normalizeSelectFilterValue(typeVal)
      this.projectTypeSelectValue = typeVal === ALL_VALUE ? ALL_VALUE : val || undefined
      this.updateTypeOption(typeVal)
    },
    resetFilters () {
      this.sortingAttributes = lodash.cloneDeep(sortingAttributes)
      this.projectTypeSelectValue = undefined
      this.setHotbLastSearchedAttributes({})
      this.setTenantProjectLastSearchedAttributes({})
      this.setStatusOptions(null)
    },
    async queryTenancyAllProjects () {
      this.setHotbLastSearchedAttributes(this.sortingAttributes)
      this.setTenantProjectLastSearchedAttributes(this.sortingAttributes)
      this.hotbLoadAll({hotbs: []})
      this.tenantProjectLoadAll({allTenantProjects: []})
      try {
        this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
        const [hotbParsedQueryParams, projectParsedQueryParams] = this.getParsedAttributes()
        if (this.canAccessHotbs && !Object.keys(tenantProjectTypes).includes(hotbParsedQueryParams.hotb.type)) {
          this.loadAllHotbs(hotbParsedQueryParams)
        }
        if (this.canAccessProjects && !Object.keys(hotbTypes).includes(hotbParsedQueryParams.hotb.type)) {
          this.loadAllTenantProjects(projectParsedQueryParams)
        }
      } finally {
        this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
      }
    },
    getParsedAttributes () {
      let sortingAttributes = lodash.cloneDeep(this.sortingAttributes)
      sortingAttributes.project.tenancyId = this.currentTenancy.id
      sortingAttributes.project.type = normalizeSelectFilterValue(sortingAttributes.project.type)
      sortingAttributes.project.floor = normalizeSelectFilterValue(sortingAttributes.project.floor)
      sortingAttributes.project.status = normalizeSelectFilterValue(sortingAttributes.project.status)

      const hotbParsedQueryParams = {
        hotb: {
          tenancyId: sortingAttributes.project.tenancyId,
          type: sortingAttributes.project.type,
          premises: sortingAttributes.project.premisesLocation,
          floor: sortingAttributes.project.floor,
          status: sortingAttributes.project.status,
          date: {},
        },
      }
      const projectParsedQueryParams = {
        project: {
          tenancyId: sortingAttributes.project.tenancyId,
          type: sortingAttributes.project.type || '',
          projectLocation: sortingAttributes.project.premisesLocation,
          floors: sortingAttributes.project.floor,
          status: sortingAttributes.project.status,
          description: sortingAttributes.project.premisesLocation,
        },
      }

      if (sortingAttributes.startDay) {
        hotbParsedQueryParams.hotb.date.startDay = sortingAttributes.startDay
        projectParsedQueryParams.statusChangedStartDay = sortingAttributes.startDay
      }

      if (sortingAttributes.endDay) {
        sortingAttributes.endDay = new Date(Date.parse(sortingAttributes.endDay) + dateUtil.ONE_DAY_MILLI - 1)
        hotbParsedQueryParams.hotb.date.endDay = sortingAttributes.endDay
        projectParsedQueryParams.statusChangedEndDay = sortingAttributes.endDay
      }

      if (!sortingAttributes.project.status) {
        delete hotbParsedQueryParams.hotb.status
        delete projectParsedQueryParams.project.status
        hotbParsedQueryParams.hotb['^status'] = hotbStatus.CANCELLED
        projectParsedQueryParams.project['^status'] = tenantProjectStatus.CANCELLED
      }

      return [hotbParsedQueryParams, projectParsedQueryParams]
    },
  },
}
</script>

<style lang="scss" scoped src="styles/viewAll.scss"></style>
<style lang="scss" scoped src="styles/dashboard/style.scss"></style>
<style lang="scss" scoped src="styles/dashboard/datatable.scss"></style>
<style lang="scss">
.tenancy-all-projects-wrapper {
  .all-projects-table {
    .date {
      min-width: 291px;
      width: 19%;
    }
    #search-panel {
      th {
        text-transform: none;
      }

      .date-picker {
        display: flex;
        align-items: center;
      }

      .vdp-datepicker {
        width: 135px;
      }
    }
    .type {
      min-width: 141px;
      width: 10%;
    }
    .project-type {
      min-width: 100px;
      width: 15%;
    }
    .premises-location {
      min-width: 100px;
      width: 45%;
    }
    .floor {
      width: 8%;
    }
    .status {
      min-width: 143px;
      font-weight: bold;
      width: 12%;
    }
  }
  .tab-header-panel {
    padding: 0px 0px 15px;
  }
}
</style>
